import type { StatusTone } from '@/lib/status-tones'

/** A calendar date the API sends as YYYY-MM-DD: "Oct 5, 2026", or "Not set". */
export function formatQuoteDate(value: string | null | undefined) {
  return value
    ? new Date(`${value}T00:00:00Z`).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'UTC',
      })
    : 'Not set'
}

/** How close the quote-by date is, as a label and a tone for the due-date chip. */
export function quoteDueDateStatus(
  value: string | null | undefined,
  now = new Date(),
): { label: string; tone: StatusTone } {
  if (!value) return { label: 'Quote by', tone: 'neutral' }
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
  const days = Math.round((new Date(`${value}T00:00:00Z`).getTime() - today) / 86_400_000)
  if (days < 0) return { label: 'Overdue', tone: 'danger' }
  if (days === 0) return { label: 'Due today', tone: 'danger' }
  if (days <= 3) return { label: `Due in ${days} ${days === 1 ? 'day' : 'days'}`, tone: 'warning' }
  if (days <= 7) return { label: `Due in ${days} days`, tone: 'progress' }
  return { label: 'Quote by', tone: 'neutral' }
}

/** "just now", "12m ago", "3h ago", "2d ago", then "Oct 5". */
export function formatRelativeTime(value: string, now = Date.now()) {
  const minutes = Math.max(0, Math.round((now - new Date(value).getTime()) / 60_000))
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.round(hours / 24)
  if (days < 7) return `${days}d ago`
  return new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

const activityLabels: Record<string, string> = {
  quote_created: 'Created',
  quote_updated: 'Updated',
  line_added: 'Product added',
  line_updated: 'Product updated',
  line_removed: 'Product removed',
  quote_shared: 'Shared',
  quote_viewed: 'Viewed',
  quote_expired: 'Expired',
  quote_approved: 'Approved',
  quote_rejected: 'Declined',
  quote_published: 'Published',
  quote_sent: 'Sent to customer',
  share_revoked: 'Customer link turned off',
  customer_comment_added: 'Customer comment',
  customer_decision_recorded: 'Customer decision',
  customer_opened: 'Customer opened',
  follow_up_reminders_paused: 'Follow-up reminders off',
  follow_up_reminders_resumed: 'Follow-up reminders on',
  quote_marked_lost: 'Marked lost',
  quote_reopened: 'Reopened',
  version_restored: 'Version restored',
  shipping_updated: 'Shipping updated',
  purchase_order_approved: 'PO approved',
  aqproj_imported: 'File imported',
  document_import_inserted: 'File imported',
}

export function activityLabel(activityType: string | undefined | null) {
  return (activityType && activityLabels[activityType]) || 'Updated'
}
